import { doorColours, doorCopy } from '@planner/config';
import { useState } from 'react';
import { reviewPresentation } from '../../appdata.js';
import type { CartLine } from '../lib/cart.js';
import { formatPrice } from '../lib/format.js';
import { doorPreviewImage } from './DoorPreview.js';
import { CartIcon } from './CartIcon.js';

function Rows({ rows }: { rows: { label: string; value: string }[] }) {
  return (
    <dl className="divide-y divide-gray-100 border-y border-gray-100">
      {rows.map((row) => (
        <div
          key={row.label}
          className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-4 py-2 text-[13px]"
        >
          <dt className="min-w-0 text-gray-500">{row.label}</dt>
          <dd className="max-w-44 break-words text-right font-semibold text-gray-800">
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function CartLineCard({
  line,
  disabled,
  canExport,
  onEdit,
  onRemove,
  onDownload,
}: {
  line: CartLine;
  disabled: boolean;
  canExport: boolean;
  onEdit(): void;
  onRemove(): void;
  onDownload(): void;
}) {
  const [confirmRemove, setConfirmRemove] = useState(false);
  const { door, item, size } = line;
  const colour = doorColours.find((candidate) => candidate.name === door.snapshot.colourName);
  const image =
    colour &&
    doorPreviewImage({
      doorType: door.doorType,
      frameColour: colour.name,
      lock: { side: door.lockSide, height: String(door.lockHeight) },
    });
  const designRows = [
    { label: 'Item Number:', value: door.snapshot.bunningsItemNumber || item.sku },
    { label: 'Type:', value: doorCopy[door.doorType].title },
    {
      label: door.doorType === 'security' ? 'Mesh:' : 'Infill:',
      value: reviewPresentation.mesh[door.doorType],
    },
    { label: 'Frame Colour:', value: door.snapshot.colourName },
    { label: 'Handle/Lock & Hinge Colour:', value: reviewPresentation.hardware },
  ];
  const makeRows = [
    { label: 'Door Height:', value: `${size.height}mm` },
    { label: 'Door Width:', value: `${size.width}mm` },
    { label: 'Lock Side:', value: door.lockSide === 'left' ? 'Left' : 'Right' },
    { label: 'Lock Height:', value: `${door.lockHeight}mm` },
    { label: 'Quantity:', value: String(item.quantity) },
  ];
  const download = (
    <button
      type="button"
      data-name="cart-download-drawing"
      onClick={onDownload}
      disabled={!canExport}
      className="inline-flex min-h-11 w-full items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 transition-colors hover:border-progress hover:text-progress disabled:cursor-not-allowed disabled:opacity-40 md:w-auto"
    >
      <CartIcon name="download" /> Download Drawing
    </button>
  );

  return (
    <article
      data-name="cart-door"
      className="relative mb-6 overflow-hidden rounded-xl border border-gray-200 bg-white"
    >
      <div className="flex items-center justify-between gap-3 bg-gray-900 px-4 py-3 sm:px-5">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-progress/15 text-progress">
            <CartIcon name="door" />
          </span>
          <h2 className="truncate text-sm font-semibold text-white sm:text-base">
            {door.doorName}
          </h2>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            data-name="cart-edit"
            onClick={onEdit}
            disabled={disabled}
            className="inline-flex min-h-10 items-center gap-1.5 rounded-lg px-2 text-xs font-medium text-white/70 hover:text-progress disabled:opacity-40"
          >
            <CartIcon name="pencil" /> Edit Design
          </button>
          <button
            type="button"
            data-name="cart-remove"
            onClick={() => setConfirmRemove(true)}
            disabled={disabled}
            aria-label={`Remove ${door.doorName} from cart`}
            className="inline-flex min-h-10 items-center gap-1.5 rounded-lg px-2 text-xs font-medium text-white/70 hover:text-progress disabled:opacity-40"
          >
            <CartIcon name="trash" /> Remove
          </button>
        </div>
      </div>
      {confirmRemove && (
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 bg-page px-4 py-3 sm:px-5">
          <span className="mr-1 text-sm text-gray-700">
            Remove {door.doorName} from your cart? Previous references and documents will remain
            valid.
          </span>
          <button
            type="button"
            data-name="cart-confirm-remove"
            disabled={disabled}
            onClick={() => {
              setConfirmRemove(false);
              onRemove();
            }}
            className="min-h-10 rounded-xl bg-gray-900 px-4 text-sm font-semibold text-white disabled:opacity-40"
          >
            Yes, remove
          </button>
          <button
            type="button"
            data-name="cart-cancel-remove"
            disabled={disabled}
            onClick={() => setConfirmRemove(false)}
            className="min-h-10 rounded-xl border border-gray-200 bg-white px-4 text-sm text-gray-600"
          >
            Cancel
          </button>
        </div>
      )}
      {line.errors.length > 0 && (
        <div
          role="alert"
          className="border-b border-gray-100 bg-progress/5 px-5 py-3 text-sm font-medium text-progress"
        >
          {line.errors.map((error) => (
            <p key={error}>{error}</p>
          ))}
        </div>
      )}
      <div className="px-5 pt-5 md:hidden">{download}</div>
      <div className="grid grid-cols-1 gap-6 p-5 pt-4 sm:p-6 md:grid-cols-[1fr_1fr_auto] md:gap-8 md:pt-6">
        <div>
          <h3 className="mb-3 text-xs font-semibold tracking-wide text-gray-400 uppercase">
            Design
          </h3>
          <Rows rows={designRows} />
          <div className="mt-4 hidden md:block">{download}</div>
        </div>
        <div>
          <h3 className="mb-3 text-xs font-semibold tracking-wide text-gray-400 uppercase">
            Door we&apos;ll make
          </h3>
          <Rows rows={makeRows} />
          <p className="mt-4 text-[13px] leading-relaxed text-gray-400">
            Final door dimensions drawing is attached to the printout / PDF.
          </p>
        </div>
        <figure className="mx-auto w-36">
          <div className="flex h-52 items-center justify-center rounded-lg bg-page p-2">
            {image ? (
              <img
                src={image.src}
                alt={image.alt}
                style={{ transform: image.mirrored ? 'scaleX(-1)' : undefined }}
                className="h-full w-full object-contain"
              />
            ) : (
              <p className="text-center text-xs text-gray-500">Door preview unavailable</p>
            )}
          </div>
          <figcaption className="mt-3 text-center">
            <span className="block text-xs text-gray-500">{door.doorName} Price</span>
            <span className="block text-2xl font-bold text-progress">
              {line.errors.length === 0 ? formatPrice(item.unitPrice * item.quantity) : '—'}
            </span>
            <span className="block text-xs text-gray-500">{formatPrice(item.unitPrice)} each</span>
          </figcaption>
        </figure>
      </div>
    </article>
  );
}
